import Tooltip, { type PositionType, type TriggerProps } from '@atlaskit/tooltip';
import React, { type ReactNode, useCallback, useRef } from 'react';

export interface TruncatedTooltipProps {
  content: string;
  children: ReactNode | ((props: TriggerProps) => ReactNode);
  position?: PositionType;
  testId?: string;
}

/**
 * A tooltip that only appears when any descendant element is truncated.
 * Checks for both horizontal overflow (text-overflow: ellipsis) and
 * vertical overflow (-webkit-line-clamp).
 */
const TruncatedTooltip = ({
  content,
  children,
  position = 'top',
  testId
}: TruncatedTooltipProps) => {
  const ref = useRef<HTMLElement | null>(null);

  const canAppear = useCallback(() => {
    const el = ref.current;
    if (!el) {
      return false;
    }
    if (el.scrollWidth > el.clientWidth) {
      return true;
    }
    // Check all descendant elements for truncation in a single pass.
    // Horizontal overflow (scrollWidth > clientWidth) catches standard
    // text-overflow: ellipsis. Vertical overflow (scrollHeight > clientHeight)
    // catches -webkit-line-clamp used by smart links with truncateInline.
    for (const child of el.querySelectorAll('*')) {
      if (child.scrollWidth > child.clientWidth) {
        return true;
      }
      if (child instanceof HTMLElement) {
        const style = getComputedStyle(child);
        if (style.webkitLineClamp && style.webkitLineClamp !== 'none') {
          if (child.scrollHeight > child.clientHeight + 1) {
            return true;
          }
        }
      }
    }
    return false;
  }, []);

  const tooltipChildren =
    typeof children === 'function' ? (
      (triggerProps: TriggerProps) =>
        children({
          ...triggerProps,
          ref: (node) => {
            ref.current = node;
            triggerProps.ref(node);
          }
        })
    ) : (
      <div ref={ref as React.Ref<HTMLDivElement>}>{children}</div>
    );

  return (
    <Tooltip content={content} position={position} canAppear={canAppear} testId={testId}>
      {tooltipChildren}
    </Tooltip>
  );
};

export default TruncatedTooltip;
